import PropTypes, { func } from 'prop-types';
import React from 'react';
import {
  AddAssetEntryCard,
  EntryCardSVGIcons,
  DomainInputView,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Textarea,
  mergeClasses,
  ProgressBar,
  Subtitle2,
  Divider,
  Body2,
  tokens,
} from '@fluentui/react-components';
import { Dismiss20Regular, StyleGuide24Regular } from '@fluentui/react-icons';
import { isValidUrl } from '@bingads-webui/url-util';
import { WarningDialogueContent } from './warning-dialogue-content';
import {
  useCommonDialogueStyles,
  useCreateWithWebsiteDialogueStyles,
} from './style';

const CreateWithWebsiteDialogueContent = ({
  i18n,
  isValidDomain,
  onChangeValidDomain,
  brandKitCreationPayload,
  isNewBrandCreateWarningEnabled,
  onChangeBrandKitCreationPayload,
}) => {
  const commonClasses = useCommonDialogueStyles();
  const dialogueClasses = useCreateWithWebsiteDialogueStyles();

  const handleDomainChange = (domain) => {
    onChangeBrandKitCreationPayload({
      ...brandKitCreationPayload,
      domain,
    });
    onChangeValidDomain(isValidUrl(domain));
  };

  const handleDescriptionChange = (e) => {
    onChangeBrandKitCreationPayload({
      ...brandKitCreationPayload,
      description: e.target.value,
    });
  };

  if (isNewBrandCreateWarningEnabled) {
    return <WarningDialogueContent i18n={i18n} />;
  }

  return (
    <div className={mergeClasses(
      'ads-studio-banner-create-with-website-dialogue-content',
      commonClasses.dialogueContentContainer
      )}
    >
      <Field
        label={{
          size: 'small',
          weight: 'semibold',
          children: i18n.getString(_TL_('Final URL:')),
        }}
      >
        <DomainInputView
          i18n={i18n}
          isDomainInvalid={!isValidDomain}
          onDomainChange={handleDomainChange}
          domainInputWrapperClassName={dialogueClasses.domainInputWrapper}
          errorMessageClassName={mergeClasses('invalid-url-error', dialogueClasses.suggestionErrorInvalid)}
          errorMessage={i18n.getString(_TL_('Please enter a valid Final URL to see images from your website.'))}
        />
      </Field>
      <Field
        label={{
          size: 'small',
          weight: 'semibold',
          children: i18n.getString(_TL_('What’s your goal for creating a new kit? This helps us better align with your brand.')),
        }}
      >
        <div className={commonClasses.searchBoxWrapper}>
          <Textarea
            size="medium"
            appearance="outline"
            className={mergeClasses('search-area', commonClasses.searchBox)}
            placeholder={i18n.getString(_TL_('Enter description'))}
            value={brandKitCreationPayload.description}
            onChange={handleDescriptionChange}
          />
        </div>
      </Field>
    </div>
  );
};

CreateWithWebsiteDialogueContent.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  isValidDomain: PropTypes.bool.isRequired,
  onChangeValidDomain: PropTypes.func.isRequired,
  brandKitCreationPayload: PropTypes.shape({
    domain: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onChangeBrandKitCreationPayload: PropTypes.func.isRequired,
  isNewBrandCreateWarningEnabled: PropTypes.bool.isRequired,
};

const CreateWithWebsiteDialogue = ({
  i18n,
  onCreateBrandKit,
  disableNewBrandKitWarning,
  isBrandKitCreationInProgress,
}) => {
  const commonClasses = useCommonDialogueStyles();
  const [isDialogueOpen, setIsDialogueOpen] = React.useState(false);
  const [
    isNewBrandCreateWarningEnabled,
    setNewBrandCreateWarningEnabled,
  ] = React.useState(!disableNewBrandKitWarning);
  const [isValidDomain, setIsValidDomain] = React.useState(true);
  const [brandKitCreationPayload, setBrandKitCreationPayload] = React.useState({
    domain: '',
    description: '',
  });

  const handleDialogueOpenChange = (dialogueData) => {
    setIsDialogueOpen(dialogueData.open);
    if (!dialogueData.open) {
      setBrandKitCreationPayload({
        domain: '',
        description: '',
      });
      setNewBrandCreateWarningEnabled(true);
    }
  };

  const handleCreateBrandKit = () => {
    if (!brandKitCreationPayload.domain || !isValidDomain) {
      return;
    }
    onCreateBrandKit(brandKitCreationPayload);
    console.info('Creating brand kit with payload:', brandKitCreationPayload);
  };

  return (
    <Dialog open={isDialogueOpen} onOpenChange={(_e, data) => handleDialogueOpenChange(data)}>
      <DialogTrigger disableButtonEnhancement>
        <AddAssetEntryCard
          onClick={() => setIsDialogueOpen(true)}
          cardClassName="create-with-website-card"
          description={i18n.getString(_TL_('Create with Website'))}
          title={i18n.getString(_TL_('Enter your URL'))}
          imageSrc={EntryCardSVGIcons.WebsiteSVGIcon}
          isFillWidth
        />
      </DialogTrigger>
      <DialogSurface className={mergeClasses(
        'ads-studio-banner-create-with-website-dialogue',
        commonClasses.container
        )}
      >
        <DialogBody>
          <DialogTitle>
            <div className={commonClasses.header}>
              <div>
                <StyleGuide24Regular />
                <Subtitle2 className="header-title">
                  {isNewBrandCreateWarningEnabled
                    ? i18n.getString(_TL_('Create New Brand Kit'))
                    : i18n.getString(_TL_('Create Brand Kit with URL'))}
                </Subtitle2>
              </div>
              <DialogTrigger disableButtonEnhancement>
                <Dismiss20Regular
                  className={mergeClasses(
                    'close-dialogue-button',
                    commonClasses.dismissIcon
                  )}
                />
              </DialogTrigger>
            </div>
            <Divider className={commonClasses.horizontalDivider} />
          </DialogTitle>
          <DialogContent>
            <CreateWithWebsiteDialogueContent
              i18n={i18n}
              isValidDomain={isValidDomain}
              onChangeValidDomain={setIsValidDomain}
              brandKitCreationPayload={brandKitCreationPayload}
              onChangeBrandKitCreationPayload={setBrandKitCreationPayload}
              isNewBrandCreateWarningEnabled={isNewBrandCreateWarningEnabled}
            />
            {isBrandKitCreationInProgress && (
              <>
                <Body2
                  className={commonClasses.loadingText}
                  color={tokens.colorNeutralStroke1}
                >
                  {i18n.getString(_TL_('Finding brand elements...'))}
                </Body2>
                <div className={commonClasses.loadingContainer}>
                  <ProgressBar thickness="large" />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions
            className={
              isNewBrandCreateWarningEnabled
                ? commonClasses.warningFooter
                : commonClasses.footer
            }
          >
            {isNewBrandCreateWarningEnabled ? (
              <Button
                appearance="primary"
                className="create-new-kit-button"
                onClick={() => setNewBrandCreateWarningEnabled(false)}
              >
                {i18n.getString(_TL_('Create new kit'))}
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleCreateBrandKit}
                className="create-brand-kit-button"
                disabled={!brandKitCreationPayload.domain || !isValidDomain}
              >
                {i18n.getString(_TL_('Create'))}
              </Button>
            )}
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" className="cancel-dialogue-button">
                {i18n.getString(_TL_('Cancel'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export { CreateWithWebsiteDialogue };

CreateWithWebsiteDialogue.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  disableNewBrandKitWarning: PropTypes.bool,
  onCreateBrandKit: PropTypes.func.isRequired,
  isBrandKitCreationInProgress: PropTypes.bool,
};

CreateWithWebsiteDialogue.defaultProps = {
  disableNewBrandKitWarning: false,
  isBrandKitCreationInProgress: false,
};
